export const state = {
    currentMember: null,
    recentOrders: [],
    orderItems: [],
    paymentMethod: 'cash',
    paymentReference: null,
    transactionState: 'idle', // idle | member-loaded | order-building | confirming | confirmed
    loading: false,
    submitting: false,
    error: null,
    confirmation: null,
    posProducts: [],
    productsLoading: false,
    pendingOrders: [],
    handoveringOrderId: null,
    memberSearchResults: [],
    memberSearchLoading: false,
};

export const getters = {
    hasMember(state) {
        return state.currentMember !== null;
    },
    orderTotal(state) {
        return state.orderItems.reduce(
            (total, item) => total + (parseFloat(item.price) * item.quantity), 0
        );
    },
    orderItemCount(state) {
        return state.orderItems.reduce((count, item) => count + item.quantity, 0);
    },
    isSubmitting(state) {
        return state.submitting;
    },
    memberName(state) {
        return state.currentMember ? state.currentMember.member_name : '';
    },
    pendingOrderCount(state) {
        return state.pendingOrders.length;
    },
};

export const actions = {
    lookupMember({ commit }, payload) {
        return new Promise((resolve, reject) => {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            window.axios
                .post('api/pos/lookup_member', payload)
                .then((response) => {
                    commit('SET_CURRENT_MEMBER', response.data);
                    commit('SET_RECENT_ORDERS', response.data.recent_orders || []);
                    commit('SET_TRANSACTION_STATE', 'member-loaded');
                    commit('SET_LOADING', false);
                    resolve(response.data);
                })
                .catch((error) => {
                    const msg = error.response?.data?.error || 'Member lookup failed';
                    commit('SET_ERROR', { message: msg, status: error.response?.status });
                    commit('SET_LOADING', false);
                    reject(error);
                });
        });
    },

    clearMember({ commit }) {
        commit('SET_CURRENT_MEMBER', null);
        commit('SET_RECENT_ORDERS', []);
        commit('SET_ORDER_ITEMS', []);
        commit('SET_TRANSACTION_STATE', 'idle');
        commit('SET_ERROR', null);
    },

    addToOrder({ commit, state }, product) {
        const existing = state.orderItems.find(
            (item) => item.product_id === product.id
        );
        if (existing) {
            commit('UPDATE_ORDER_ITEM_QTY', {
                product_id: product.id,
                quantity: existing.quantity + 1,
            });
        } else {
            commit('ADD_ORDER_ITEM', {
                product_id: product.id,
                name: product.name,
                price: product.price,
                quantity: 1,
                stock_quantity: product.stock_quantity,
                product_img_src: product.product_img_src,
            });
        }
        commit('SET_TRANSACTION_STATE', 'order-building');
    },

    updateQuantity({ commit }, { product_id, quantity }) {
        if (quantity < 1) return;
        commit('UPDATE_ORDER_ITEM_QTY', { product_id, quantity });
    },

    removeItem({ commit, state }, product_id) {
        commit('REMOVE_ORDER_ITEM', product_id);
        if (state.orderItems.length === 0) {
            commit('SET_TRANSACTION_STATE', 'member-loaded');
        }
    },

    confirmOrder({ commit, state }, branchId) {
        return new Promise((resolve, reject) => {
            commit('SET_SUBMITTING', true);
            commit('SET_ERROR', null);
            commit('SET_TRANSACTION_STATE', 'confirming');
            window.axios
                .post('api/pos/create_order', {
                    client_id: state.currentMember.id,
                    branch_id: branchId,
                    items: state.orderItems.map((i) => ({
                        product_id: i.product_id,
                        quantity: i.quantity,
                    })),
                    payment_method: state.paymentMethod,
                    payment_reference: state.paymentReference,
                })
                .then((response) => {
                    commit('SET_CONFIRMATION', response.data);
                    commit('SET_TRANSACTION_STATE', 'confirmed');
                    commit('SET_SUBMITTING', false);
                    resolve(response.data);
                })
                .catch((error) => {
                    commit('SET_ERROR',
                        error.response?.data || { message: 'Order failed' }
                    );
                    commit('SET_TRANSACTION_STATE', 'order-building');
                    commit('SET_SUBMITTING', false);
                    reject(error);
                });
        });
    },

    resetTransaction({ commit }) {
        commit('RESET_TRANSACTION');
    },

    setPaymentMethod({ commit }, method) {
        commit('SET_PAYMENT_METHOD', method);
    },

    setPaymentReference({ commit }, reference) {
        commit('SET_PAYMENT_REFERENCE', reference);
    },

    fetchPendingOrders({ commit, state }, branchId) {
        if (!state.currentMember) return;
        let url = `api/pos/pending_orders/${state.currentMember.id}`;
        if (branchId) {
            url += `?branch_id=${branchId}`;
        }
        window.axios
            .get(url)
            .then((response) => {
                commit('SET_PENDING_ORDERS', response.data);
            })
            .catch(() => {
                commit('SET_PENDING_ORDERS', []);
            });
    },

    processHandover({ commit }, orderId) {
        commit('SET_HANDOVERING_ORDER_ID', orderId);
        return new Promise((resolve, reject) => {
            window.axios
                .post('api/pos/handover_order', { order_id: orderId })
                .then((response) => {
                    commit('REMOVE_PENDING_ORDER', orderId);
                    commit('SET_HANDOVERING_ORDER_ID', null);
                    resolve(response.data);
                })
                .catch((error) => {
                    commit('SET_ERROR', error.response?.data || { message: 'Handover failed' });
                    commit('SET_HANDOVERING_ORDER_ID', null);
                    reject(error);
                });
        });
    },

    searchMembers({ commit }, query) {
        if (!query || query.length < 2) {
            commit('SET_MEMBER_SEARCH_RESULTS', []);
            return Promise.resolve([]);
        }
        commit('SET_MEMBER_SEARCH_LOADING', true);
        return new Promise((resolve, reject) => {
            window.axios
                .post('api/pos/search_members', { query })
                .then((response) => {
                    commit('SET_MEMBER_SEARCH_RESULTS', response.data);
                    commit('SET_MEMBER_SEARCH_LOADING', false);
                    resolve(response.data);
                })
                .catch((error) => {
                    commit('SET_MEMBER_SEARCH_RESULTS', []);
                    commit('SET_MEMBER_SEARCH_LOADING', false);
                    reject(error);
                });
        });
    },

    clearMemberSearch({ commit }) {
        commit('SET_MEMBER_SEARCH_RESULTS', []);
    },

    fetchPosProducts({ commit, state }) {
        if (!state.currentMember) return Promise.reject('No member loaded');
        commit('SET_PRODUCTS_LOADING', true);
        return new Promise((resolve, reject) => {
            window.axios
                .get(`api/pos/products/${state.currentMember.id}`)
                .then((response) => {
                    commit('SET_POS_PRODUCTS', response.data);
                    commit('SET_PRODUCTS_LOADING', false);
                    resolve(response.data);
                })
                .catch((error) => {
                    commit('SET_ERROR', error.response?.data || { message: 'Failed to load products' });
                    commit('SET_PRODUCTS_LOADING', false);
                    reject(error);
                });
        });
    },
};

export const mutations = {
    SET_CURRENT_MEMBER(state, member) {
        state.currentMember = member;
    },
    SET_RECENT_ORDERS(state, orders) {
        state.recentOrders = orders;
    },
    SET_ORDER_ITEMS(state, items) {
        state.orderItems = items;
    },
    ADD_ORDER_ITEM(state, item) {
        state.orderItems.push(item);
    },
    UPDATE_ORDER_ITEM_QTY(state, { product_id, quantity }) {
        const item = state.orderItems.find((i) => i.product_id === product_id);
        if (item) item.quantity = quantity;
    },
    REMOVE_ORDER_ITEM(state, product_id) {
        state.orderItems = state.orderItems.filter(
            (i) => i.product_id !== product_id
        );
    },
    SET_PAYMENT_METHOD(state, method) {
        state.paymentMethod = method;
    },
    SET_PAYMENT_REFERENCE(state, reference) {
        state.paymentReference = reference;
    },
    SET_TRANSACTION_STATE(state, transactionState) {
        state.transactionState = transactionState;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_SUBMITTING(state, submitting) {
        state.submitting = submitting;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_CONFIRMATION(state, confirmation) {
        state.confirmation = confirmation;
    },
    SET_PENDING_ORDERS(state, orders) {
        state.pendingOrders = orders;
    },
    SET_HANDOVERING_ORDER_ID(state, orderId) {
        state.handoveringOrderId = orderId;
    },
    REMOVE_PENDING_ORDER(state, orderId) {
        state.pendingOrders = state.pendingOrders.filter((o) => o.id !== orderId);
    },
    SET_POS_PRODUCTS(state, products) {
        state.posProducts = products;
    },
    SET_MEMBER_SEARCH_RESULTS(state, results) {
        state.memberSearchResults = results;
    },
    SET_MEMBER_SEARCH_LOADING(state, loading) {
        state.memberSearchLoading = loading;
    },
    SET_PRODUCTS_LOADING(state, loading) {
        state.productsLoading = loading;
    },
    RESET_TRANSACTION(state) {
        state.currentMember = null;
        state.recentOrders = [];
        state.orderItems = [];
        state.paymentMethod = 'cash';
        state.paymentReference = null;
        state.transactionState = 'idle';
        state.loading = false;
        state.submitting = false;
        state.error = null;
        state.confirmation = null;
        state.posProducts = [];
        state.productsLoading = false;
        state.pendingOrders = [];
        state.handoveringOrderId = null;
        state.memberSearchResults = [];
        state.memberSearchLoading = false;
    },
};
